import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Users as UsersIcon, Search, ChevronLeft, ChevronRight, Eye, Edit, Trash2, X, UserPlus, Shield, User, Copy, LogIn, LogOut, Clock, Ban, UserCheck, UserX, AlertTriangle, RefreshCw, ShieldAlert, ShieldCheck, Power, UserCog, CheckCheck, AlertCircle, CheckCircle, Mail } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import ConfirmationModal from '@/components/ConfirmationModal';
import { AuthContext } from '@/App';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { PasswordInput } from '@/components/ui/password-input';
import { getEmailLengthError } from '@/utils/emailValidation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ListUsers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useContext(AuthContext);
  
  // Helper function to format role display
  const formatRole = (role) => {
    const roleMap = {
      'owner': 'Owner',
      'admin': 'Admin',
      'user': 'User'
    };
    return roleMap[role] || 'User';
  };

  // Helper function to check if user is admin
  const isAdminRole = (role) => {
    return role === 'admin';
  };

  // Copy to clipboard function
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setConfirmModal({
        isOpen: true,
        title: 'Copied!',
        message: `${label} copied to clipboard`,
        type: 'success',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
      // Auto close after 1.5 seconds
      setTimeout(() => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }, 1500);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get('page')) || 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [companyFilter, setCompanyFilter] = useState(searchParams.get('companyId') || '');
  const [roleFilter, setRoleFilter] = useState(searchParams.get('role') || '');
  const [deletedFilter, setDeletedFilter] = useState(searchParams.get('status') || 'active');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [companySearch, setCompanySearch] = useState(searchParams.get('companyName') || '');
  
  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  const [expandedRow, setExpandedRow] = useState(searchParams.get('openUserId') || null);
  const [editingUser, setEditingUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [selectedFilterCompanyIndex, setSelectedFilterCompanyIndex] = useState(-1);
  
  // Separate state for edit modal
  const [editCompanySearch, setEditCompanySearch] = useState('');
  const [editFilteredCompanies, setEditFilteredCompanies] = useState([]);
  const [showEditCompanyDropdown, setShowEditCompanyDropdown] = useState(false);
  const [selectedEditCompanyIndex, setSelectedEditCompanyIndex] = useState(-1);
  
  // Password change states
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Auth events state for expanded row
  const [userAuthEvents, setUserAuthEvents] = useState({});
  const [loadingAuthEvents, setLoadingAuthEvents] = useState({});
  const [showAllEventsModal, setShowAllEventsModal] = useState(null);
  const [allEventsData, setAllEventsData] = useState({ events: [], total: 0, page: 1, totalPages: 0 });
  const [loadingAllEvents, setLoadingAllEvents] = useState(false);
  
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {},
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    showCancel: true
  });

  // New users badge count
  const [newUsersCount, setNewUsersCount] = useState(0);

  const loadNewUsersCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/new-users/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNewUsersCount(res.data.count || 0);
    } catch (err) {
      console.error('Error loading new users count:', err);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [pagination.page, search, companyFilter, roleFilter, deletedFilter]);

  // Sync filter state to URL query parameters
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (companyFilter) params.set('companyId', companyFilter);
    if (companySearch && companyFilter) params.set('companyName', companySearch);
    if (roleFilter) params.set('role', roleFilter);
    if (deletedFilter && deletedFilter !== 'active') params.set('status', deletedFilter);
    if (pagination.page > 1) params.set('page', String(pagination.page));
    if (expandedRow) params.set('openUserId', expandedRow);
    setSearchParams(params, { replace: true });
  }, [search, companyFilter, companySearch, roleFilter, deletedFilter, pagination.page, expandedRow, setSearchParams]);

  // Deep link: auto-expand user when openUserId is in URL
  useEffect(() => {
    const openId = searchParams.get('openUserId');
    if (openId && !loading && users.length > 0) {
      const found = users.find(u => u.id === openId);
      if (found) {
        setExpandedRow(openId);
        if (!userAuthEvents[openId]) {
          loadUserAuthEvents(openId);
        }
      }
    }
  }, [users, loading]);

  // Load new users count on mount
  useEffect(() => {
    loadNewUsersCount();
  }, [loadNewUsersCount]);

  // Mark users as seen when viewing "new_users" filter
  useEffect(() => {
    if (deletedFilter === 'new_users' && users.length > 0 && !loading) {
      const newUserIds = users.filter(u => u.isNewUser).map(u => u.id);
      if (newUserIds.length > 0) {
        const token = localStorage.getItem('token');
        axios.post(`${BACKEND_URL}/api/admin/new-users/mark-seen`, 
          { userIds: newUserIds },
          { headers: { 'Authorization': `Bearer ${token}` } }
        ).then(res => {
          const remaining = res.data.remainingCount ?? 0;
          setNewUsersCount(remaining);
          window.dispatchEvent(new CustomEvent('newUsersCountChanged', { detail: { count: remaining } }));
        }).catch(err => console.error('Error marking users seen:', err));
      }
    }
  }, [users, deletedFilter, loading]);

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/admin/new-users/mark-all-read`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNewUsersCount(0);
      window.dispatchEvent(new CustomEvent('newUsersCountChanged', { detail: { count: 0 } }));
      toast.success('All new user notifications marked as read');
      if (deletedFilter === 'new_users') {
        loadUsers();
      }
    } catch (err) {
      console.error('Error marking all read:', err);
      toast.error('Failed to mark all as read');
    }
  };

  const handleResetDeliverability = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/email-deliverability/reset`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Email deliverability reset to OK');
      loadUsers();
    } catch (err) {
      console.error('Error resetting deliverability:', err);
      toast.error('Failed to reset deliverability');
    }
  };

  useEffect(() => {
    if (companySearch) {
      searchCompaniesForFilter(companySearch);
    } else {
      setFilteredCompanies([]);
      setShowCompanyDropdown(false);
    }
    setSelectedFilterCompanyIndex(-1);
  }, [companySearch]);

  useEffect(() => {
    // Only search if user is actively typing (editCompanySearch changed)
    // Don't trigger on modal open
    if (editCompanySearch && editingUser) {
      searchCompaniesForEdit(editCompanySearch);
    } else {
      setEditFilteredCompanies([]);
      setShowEditCompanyDropdown(false);
    }
    setSelectedEditCompanyIndex(-1);
  }, [editCompanySearch]); // Removed editingUser from dependencies

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (editingUser) {
          handleCloseEdit();
        } else if (confirmModal.isOpen) {
          setConfirmModal({ ...confirmModal, isOpen: false });
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [editingUser, confirmModal, originalUser]);

  const searchCompaniesForFilter = async (query) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/companies/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setFilteredCompanies(response.data.companies || []);
      if (response.data.companies && response.data.companies.length > 0) {
        setShowCompanyDropdown(true);
      }
    } catch (error) {
      console.error('Error searching companies:', error);
      setFilteredCompanies([]);
    }
  };

  const searchCompaniesForEdit = async (query) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/companies/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setEditFilteredCompanies(response.data.companies || []);
      // Don't auto-open dropdown - let user interaction control it
    } catch (error) {
      console.error('Error searching companies:', error);
      setEditFilteredCompanies([]);
    }
  };

  const selectCompanyFilter = (company) => {
    setCompanyFilter(company.id);
    setCompanySearch(company.companyName);
    setShowCompanyDropdown(false);
    setSelectedFilterCompanyIndex(-1);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterCompanyKeyDown = (e) => {
    if (!showCompanyDropdown || filteredCompanies.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedFilterCompanyIndex(prev => 
          prev < filteredCompanies.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedFilterCompanyIndex(prev => 
          prev > 0 ? prev - 1 : filteredCompanies.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedFilterCompanyIndex >= 0 && selectedFilterCompanyIndex < filteredCompanies.length) {
          selectCompanyFilter(filteredCompanies[selectedFilterCompanyIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowCompanyDropdown(false);
        setSelectedFilterCompanyIndex(-1);
        break;
    }
  };

  const clearCompanyFilter = () => {
    setCompanyFilter('');
    setCompanySearch('');
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search }),
        ...(companyFilter && { companyId: companyFilter }),
        ...(roleFilter && { role: roleFilter }),
        filter: deletedFilter
      });

      const response = await axios.get(`${BACKEND_URL}/api/admin/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setUsers(response.data.users || []);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleViewToggle = async (userId) => {
    if (expandedRow === userId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(userId);
      // Load auth events for this user if not already loaded
      if (!userAuthEvents[userId]) {
        await loadUserAuthEvents(userId);
      }
    }
  };

  // Load last 5 auth events for a user
  const loadUserAuthEvents = async (userId) => {
    setLoadingAuthEvents(prev => ({ ...prev, [userId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/users/${userId}/auth-events?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUserAuthEvents(prev => ({ ...prev, [userId]: response.data.events || [] }));
    } catch (error) {
      console.error('Error loading user auth events:', error);
      setUserAuthEvents(prev => ({ ...prev, [userId]: [] }));
    } finally {
      setLoadingAuthEvents(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Load all auth events for modal
  const loadAllUserEvents = async (userId, page = 1) => {
    setLoadingAllEvents(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/users/${userId}/auth-events?page=${page}&pageSize=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAllEventsData(response.data);
    } catch (error) {
      console.error('Error loading all user auth events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoadingAllEvents(false);
    }
  };

  const handleShowAllEvents = async (userId) => {
    setShowAllEventsModal(userId);
    await loadAllUserEvents(userId);
  };

  // Admin actions
  const handleForceLogout = async (userId, userEmail) => {
    setConfirmModal({
      isOpen: true,
      title: 'Force Logout User',
      message: `All sessions for ${userEmail} will be terminated. Do you want to continue?`,
      type: 'warning',
      confirmText: 'Force Logout',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/force-logout`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success('User has been logged out');
          // Refresh auth events
          await loadUserAuthEvents(userId);
        } catch (error) {
          console.error('Error forcing logout:', error);
          toast.error('Failed to force logout');
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const [freezeModal, setFreezeModal] = useState({ isOpen: false, userId: '', userEmail: '', hours: 0, minutes: 30, loading: false });

  const handleFreezeUser = (userId, userEmail) => {
    setFreezeModal({ isOpen: true, userId, userEmail, hours: 0, minutes: 30, loading: false });
  };

  const handleImpersonate = async (userId, userEmail, userStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/admin/impersonate/${userId}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const impToken = response.data.token;
      const baseUrl = window.location.origin;
      window.open(`${baseUrl}/dashboard?impersonation_token=${impToken}`, '_blank');
      toast.success(`Impersonating ${userEmail}`);
      if (userStatus === 'FROZEN') {
        toast.warning('This user is currently frozen. Login restrictions apply.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start impersonation');
    }
  };

  const confirmFreeze = async () => {
    const totalMinutes = freezeModal.hours * 60 + freezeModal.minutes;
    if (totalMinutes < 1) {
      toast.error('Freeze duration must be at least 1 minute');
      return;
    }
    setFreezeModal(prev => ({ ...prev, loading: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/admin/users/${freezeModal.userId}/freeze`, 
        { freezeDurationMinutes: totalMinutes },
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      toast.success(response.data.message);
      loadUsers();
      await loadUserAuthEvents(freezeModal.userId);
      setFreezeModal(prev => ({ ...prev, isOpen: false }));
    } catch (error) {
      console.error('Error freezing user:', error);
      toast.error(error.response?.data?.detail || 'Failed to freeze account');
    } finally {
      setFreezeModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleUnfreezeUser = async (userId, userEmail) => {
    setConfirmModal({
      isOpen: true,
      title: 'Unfreeze Account',
      message: `${userEmail}'s account will be unfrozen and they will be able to log in again. Do you want to continue?`,
      type: 'warning',
      confirmText: 'Unfreeze',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/unfreeze`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success('User account has been unfrozen');
          // Refresh user list and auth events
          loadUsers();
          await loadUserAuthEvents(userId);
        } catch (error) {
          console.error('Error unfreezing user:', error);
          toast.error('Failed to unfreeze account');
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleUnsuspendUser = async (userId, userEmail) => {
    setConfirmModal({
      isOpen: true,
      title: 'Unsuspend Account',
      message: `${userEmail}'s account will be unsuspended and they will be able to log in again. Do you want to continue?`,
      type: 'warning',
      confirmText: 'Unsuspend',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/unsuspend`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success('User account has been unsuspended');
          // Refresh user list and auth events
          loadUsers();
          await loadUserAuthEvents(userId);
        } catch (error) {
          console.error('Error unsuspending user:', error);
          toast.error('Failed to unsuspend account');
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const [togglingActive, setTogglingActive] = useState({});

  const handleToggleActive = async (userId, userEmail, currentlyActive) => {
    const newState = !currentlyActive;
    const actionLabel = newState ? 'Enable' : 'Disable';
    
    setConfirmModal({
      isOpen: true,
      title: `${actionLabel} Account`,
      message: newState
        ? `${userEmail}'s account will be enabled and they will be able to log in again. Do you want to continue?`
        : `${userEmail}'s account will be disabled. They will not be able to log in and any active sessions will be terminated. Do you want to continue?`,
      type: 'warning',
      confirmText: actionLabel,
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        setTogglingActive(prev => ({ ...prev, [userId]: true }));
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/toggle-active`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success(response.data.message);
          // Update local state
          setUsers(prev => prev.map(u => u.id === userId ? { ...u, isActive: response.data.isActive } : u));
          await loadUserAuthEvents(userId);
        } catch (error) {
          console.error('Error toggling user active:', error);
          toast.error(error.response?.data?.detail || `Failed to ${actionLabel.toLowerCase()} account`);
        } finally {
          setTogglingActive(prev => ({ ...prev, [userId]: false }));
        }
      }
    });
  };

  // Event type helpers
  const getEventTypeConfig = (eventType) => {
    const configs = {
      LOGIN_SUCCESS: { label: 'Login Success', icon: LogIn, color: 'text-green-600', bg: 'bg-green-100' },
      LOGIN_FAIL: { label: 'Login Failed', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-100' },
      LOGOUT: { label: 'Logout', icon: LogOut, color: 'text-blue-600', bg: 'bg-blue-100' },
      SESSION_EXPIRED: { label: 'Session Expired', icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100' },
      ADMIN_FORCE_LOGOUT: { label: 'Force Logout', icon: UserX, color: 'text-orange-600', bg: 'bg-orange-100' },
      ADMIN_FREEZE_USER: { label: 'Frozen', icon: Ban, color: 'text-red-600', bg: 'bg-red-100' },
      ADMIN_UNFREEZE_USER: { label: 'Unfrozen', icon: UserCheck, color: 'text-green-600', bg: 'bg-green-100' },
      ACCOUNT_SUSPENDED: { label: 'Suspended', icon: ShieldAlert, color: 'text-purple-600', bg: 'bg-purple-100' },
      ADMIN_UNSUSPEND_USER: { label: 'Unsuspended', icon: ShieldCheck, color: 'text-teal-600', bg: 'bg-teal-100' },
      ADMIN_DISABLE_USER: { label: 'Disabled', icon: Power, color: 'text-red-600', bg: 'bg-red-100' },
      ADMIN_ENABLE_USER: { label: 'Enabled', icon: Power, color: 'text-green-600', bg: 'bg-green-100' },
      IMPERSONATION_START: { label: 'Impersonation Start', icon: UserCog, color: 'text-amber-600', bg: 'bg-amber-100' },
      IMPERSONATION_END: { label: 'Impersonation End', icon: UserCog, color: 'text-amber-600', bg: 'bg-amber-100' }
    };
    return configs[eventType] || { label: eventType, icon: Shield, color: 'text-gray-600', bg: 'bg-gray-100' };
  };

  const formatEventDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('tr-TR', { 
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const handleEdit = (user) => {
    setEditingUser({ ...user });
    setOriginalUser(JSON.parse(JSON.stringify(user)));
    if (user.companyId && user.companyName) {
      setEditCompanySearch(user.companyName);
    } else {
      setEditCompanySearch('');
    }
    setShowEditCompanyDropdown(false); // Don't show dropdown initially
  };

  const hasChanges = () => {
    if (!editingUser || !originalUser) return false;
    return JSON.stringify(editingUser) !== JSON.stringify(originalUser) || newPassword || confirmPassword;
  };

  const handleCloseEdit = () => {
    if (hasChanges()) {
      setConfirmModal({
        isOpen: true,
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to close? All changes will be lost.',
        type: 'warning',
        confirmText: 'Close Anyway',
        cancelText: 'Keep Editing',
        onConfirm: () => {
          setEditingUser(null);
          setOriginalUser(null);
          setEditCompanySearch('');
          setShowEditCompanyDropdown(false);
          setNewPassword('');
          setConfirmPassword('');
        }
      });
    } else {
      setEditingUser(null);
      setOriginalUser(null);
      setEditCompanySearch('');
      setShowEditCompanyDropdown(false);
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    const emailLengthError = getEmailLengthError(editingUser?.email);
    if (emailLengthError) {
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: emailLengthError,
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
      return;
    }

    // Validate password if provided
    if (newPassword || confirmPassword) {
      if (newPassword !== confirmPassword) {
        setConfirmModal({
          isOpen: true,
          title: 'Error',
          message: 'Passwords do not match!',
          type: 'danger',
          confirmText: 'OK',
          showCancel: false,
          onConfirm: () => {}
        });
        return;
      }
      if (newPassword.length < 6) {
        setConfirmModal({
          isOpen: true,
          title: 'Error',
          message: 'Password must be at least 6 characters long!',
          type: 'danger',
          confirmText: 'OK',
          showCancel: false,
          onConfirm: () => {}
        });
        return;
      }
    }
    
    // Save original state for error rollback
    const originalUsers = [...users];
    
    try {
      // 1. Instant UI feedback - update local state immediately
      const updatedUserData = {
        ...editingUser,
        companyName: editingUser.companyName // Keep company name
      };
      setUsers(users.map(u => u.id === editingUser.id ? updatedUserData : u));
      
      // 2. Prepare update payload
      const updatePayload = {
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        email: editingUser.email,
        companyId: editingUser.companyId,
        role: editingUser.role
      };
      
      // Add password only if provided
      if (newPassword) {
        updatePayload.password = newPassword;
      }
      
      // 3. Send update request to backend using axios
      const token = localStorage.getItem('token');
      const response = await axios.put(`${BACKEND_URL}/api/admin/users/${editingUser.id}`, 
        updatePayload,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Get updated data from backend
      const updatedUser = response.data;
      
      // Update with backend response to ensure data consistency
      setUsers(users.map(u => u.id === updatedUser.id ? updatedUser : u));

      // 4. Close modal and clear state
      setEditingUser(null);
      setOriginalUser(null);
      setEditCompanySearch('');
      setShowEditCompanyDropdown(false);
      setNewPassword('');
      setConfirmPassword('');
      
      setConfirmModal({
        isOpen: true,
        title: 'Success',
        message: newPassword ? 'User and password updated successfully!' : 'User updated successfully!',
        type: 'success',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } catch (error) {
      // Revert to original state on error
      setUsers(originalUsers);
      const detail = error.response?.data?.detail;
      let errorMessage;
      if (detail?.code === 'EMAIL_NOT_VERIFIED') {
        errorMessage = detail.message || 'User must verify email before company assignment.';
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (typeof detail === 'object' && detail?.message) {
        errorMessage = detail.message;
      } else {
        errorMessage = error.message || 'Failed to update user. Please try again.';
      }
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: errorMessage,
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    }
  };

  const handleDeleteClick = (userId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete User',
      message: 'Are you sure you want to delete this user?',
      type: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: () => handleDelete(userId)
    });
  };

  const handleApproveUser = async (userId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/approve`);
      toast.success('User approved successfully');
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, approvalStatus: 'approved' } : u));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/reject`);
      toast.success('User rejected');
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, approvalStatus: 'rejected' } : u));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject user');
    }
  };

  const handleDelete = async (userId) => {
    try {
      // 1. Instant UI feedback - immediately remove from list
      const newUsers = users.filter(u => u.id !== userId);
      setUsers(newUsers);
      
      // 2. Update pagination manually
      const newTotal = pagination.total - 1;
      const newTotalPages = Math.ceil(newTotal / pagination.limit);
      
      setPagination(prev => ({
        ...prev,
        total: newTotal,
        totalPages: newTotalPages
      }));
      
      // 3. If current page becomes empty and not first page, go to previous page
      if (newUsers.length === 0 && pagination.page > 1) {
        setPagination(prev => ({ ...prev, page: prev.page - 1 }));
        // Note: We're not reloading data, just adjusting page number
        // The previous page data will be loaded when pagination.page changes
      }
      
      // 4. Send delete request to backend
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setConfirmModal({
        isOpen: true,
        title: 'Success',
        message: 'User deleted successfully!',
        type: 'success',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      // On error, need to reload to restore correct state
      loadUsers();
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to delete user. Please try again.',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    }
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
      setSelectAll(false);
    } else {
      // Select all users except current user and super admin
      const selectableUsers = users.filter(u => 
        u.id !== currentUser?.id && u.email !== 'admin@specard.net'
      );
      setSelectedUsers(selectableUsers.map(u => u.id));
      setSelectAll(true);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedUsers.length === 0) return;
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Multiple Users',
      message: `Are you sure you want to delete ${selectedUsers.length} user(s)? This action cannot be undone.`,
      type: 'warning',
      confirmText: 'Delete All',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          
          // Delete all selected users
          await Promise.all(
            selectedUsers.map(userId =>
              axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              })
            )
          );
          
          // Clear selection
          setSelectedUsers([]);
          setSelectAll(false);
          
          // Reload users
          loadUsers();
          
          setConfirmModal({
            isOpen: true,
            title: 'Success',
            message: `${selectedUsers.length} user(s) deleted successfully!`,
            type: 'success',
            confirmText: 'OK',
            showCancel: false,
            onConfirm: () => {}
          });
        } catch (error) {
          console.error('Error deleting users:', error);
          loadUsers();
          setConfirmModal({
            isOpen: true,
            title: 'Error',
            message: 'Failed to delete some users. Please try again.',
            type: 'danger',
            confirmText: 'OK',
            showCancel: false,
            onConfirm: () => {}
          });
        }
      }
    });
  };

  const selectCompanyForEdit = (company) => {
    setEditingUser({
      ...editingUser,
      companyId: company.id,
      companyName: company.companyName
    });
    setEditCompanySearch(company.companyName);
    setShowEditCompanyDropdown(false);
    setSelectedEditCompanyIndex(-1);
  };

  const handleEditCompanyKeyDown = (e) => {
    if (!showEditCompanyDropdown || editFilteredCompanies.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedEditCompanyIndex(prev => 
          prev < editFilteredCompanies.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedEditCompanyIndex(prev => 
          prev > 0 ? prev - 1 : editFilteredCompanies.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedEditCompanyIndex >= 0 && selectedEditCompanyIndex < editFilteredCompanies.length) {
          selectCompanyForEdit(editFilteredCompanies[selectedEditCompanyIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowEditCompanyDropdown(false);
        setSelectedEditCompanyIndex(-1);
        break;
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h2 className="text-3xl font-bold mb-2" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Users
            </h2>
            <p className="text-gray-600">Manage all users in the system</p>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex gap-4 mb-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Search by name or email..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Company Filter */}
              <div className="w-64 relative">
                <input
                  type="text"
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  onKeyDown={handleFilterCompanyKeyDown}
                  placeholder="Filter by company..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                {showCompanyDropdown && filteredCompanies.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCompanies.map((company, index) => (
                      <button
                        key={company.id}
                        type="button"
                        onClick={() => selectCompanyFilter(company)}
                        className={`w-full text-left px-4 py-3 transition-colors ${
                          index === selectedFilterCompanyIndex 
                            ? 'bg-orange-100 text-orange-900' 
                            : 'hover:bg-orange-50'
                        }`}
                      >
                        {company.companyName}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Role Filter */}
              <div className="w-48">
                <Select value={roleFilter} onValueChange={(val) => { setRoleFilter(val === '_all' ? '' : val); setPagination(prev => ({ ...prev, page: 1 })); }}>
                  <SelectTrigger className="w-full h-[46px] border-gray-300 focus:ring-2 focus:ring-orange-500" data-testid="role-filter">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Roles</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div className="w-48">
                <Select value={deletedFilter} onValueChange={(val) => { setDeletedFilter(val); setPagination(prev => ({ ...prev, page: 1 })); }}>
                  <SelectTrigger className="w-full h-[46px] border-gray-300 focus:ring-2 focus:ring-orange-500" data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="new_users">
                      <span className="flex items-center gap-2">
                        New Users
                        {newUsersCount > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold">
                            {newUsersCount}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                    <SelectItem value="frozen">Frozen</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                    <SelectItem value="unverified">Unverified Email</SelectItem>
                    <SelectItem value="pending_approval">Pending Approval</SelectItem>
                    <SelectItem value="external_signup">External Signups</SelectItem>
                    <SelectItem value="deleted">Deleted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Search Button */}
              <button
                onClick={handleSearch}
                className="text-white px-6 py-3 rounded-lg hover:opacity-90 transition-all"
                style={{ background: '#FF6600' }}
              >
                Search
              </button>

              {/* Clear Filters Button */}
              {(searchInput || companyFilter || roleFilter || deletedFilter !== 'active') && (
                <button
                  onClick={() => {
                    setSearchInput('');
                    setSearch('');
                    setCompanySearch('');
                    setCompanyFilter('');
                    setRoleFilter('');
                    setDeletedFilter('active');
                    setExpandedRow(null);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4" />
                  Clear Filters
                </button>
              )}
            </div>

            {/* Action Buttons Row */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => navigate('/admin/user-settings/create')}
                className="text-white px-6 py-2.5 rounded-lg hover:opacity-90 transition-all flex items-center gap-2"
                style={{ background: '#FF6600' }}
              >
                <UserPlus className="w-5 h-5" />
                Create User
              </button>

              <button
                onClick={handleBulkDelete}
                disabled={selectedUsers.length === 0 || deletedFilter === 'deleted'}
                className="bg-red-600 text-white px-6 py-2.5 rounded-lg hover:bg-red-700 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-5 h-5" />
                Delete Selected ({selectedUsers.length})
              </button>

              {deletedFilter === 'new_users' && (newUsersCount > 0 || users.length > 0) && (
                <button
                  onClick={handleMarkAllRead}
                  className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-4 h-4" />
                  Mark All as Read
                </button>
              )}
            </div>

            {/* Active Filters */}
            {(search || companyFilter || roleFilter || deletedFilter !== 'active') && (
              <div className="mt-4 flex gap-2 flex-wrap">
                {search && (
                  <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">
                    Search: {search}
                    <button onClick={() => { setSearch(''); setSearchInput(''); }} className="hover:text-orange-900">×</button>
                  </span>
                )}
                {companyFilter && (
                  <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">
                    Company: {companySearch}
                    <button onClick={clearCompanyFilter} className="hover:text-orange-900">×</button>
                  </span>
                )}
                {roleFilter && (
                  <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">
                    Role: {formatRole(roleFilter)}
                    <button onClick={() => setRoleFilter('')} className="hover:text-orange-900">×</button>
                  </span>
                )}
                {deletedFilter === 'new_users' && (
                  <span className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                    New Users
                    <button onClick={() => setDeletedFilter('active')} className="hover:text-green-900">×</button>
                  </span>
                )}
                {deletedFilter === 'deleted' && (
                  <span className="inline-flex items-center gap-2 bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
                    Only Deleted
                    <button onClick={() => setDeletedFilter('active')} className="hover:text-red-900">×</button>
                  </span>
                )}
                {deletedFilter === 'unverified' && (
                  <span className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold" data-testid="unverified-filter-badge">
                    Unverified Email
                    <button onClick={() => setDeletedFilter('active')} className="hover:text-yellow-900">×</button>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="text-gray-500">Loading users...</div>
              </div>
            ) : users.length === 0 ? (
              <div className="p-12 text-center">
                <UsersIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No users found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto" data-testid="users-table-scroll">
                <table className="w-full table-fixed min-w-[1040px]" data-testid="users-table">
                  <colgroup>
                    <col className="w-[68px]" />
                    <col className="w-[14%]" />
                    <col className="w-[24%]" />
                    <col className="w-[14%]" />
                    <col className="w-[150px]" />
                    <col className="w-[86px]" />
                    <col className="w-[90px]" />
                    <col className="w-[180px]" />
                  </colgroup>
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 w-12">
                        <Checkbox
                          checked={selectAll}
                          onCheckedChange={handleSelectAll}
                          disabled={deletedFilter === 'deleted'}
                        />
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Company</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Company Type</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Role</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Projects</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {users.map((user) => {
                      const isSelectable = user.id !== currentUser?.id && user.email !== 'admin@specard.net';
                      const isSelected = selectedUsers.includes(user.id);
                      const hasEmailBadges = !user.emailVerified
                        || user.approvalStatus === 'pending'
                        || user.approvalStatus === 'rejected'
                        || user.emailDeliverabilityStatus === 'hard_bounced'
                        || user.emailDeliverabilityStatus === 'complained';
                      
                      return (
                      <React.Fragment key={user.id}>
                        <tr className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            {isSelectable ? (
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => handleSelectUser(user.id)}
                                disabled={deletedFilter === 'deleted'}
                              />
                            ) : (
                              <div className="w-5 h-5" />
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div
                              className="font-medium text-gray-900 truncate"
                              data-testid={`user-name-${user.id}`}
                              title={user.firstName && user.lastName
                                ? `${user.firstName} ${user.lastName}`
                                : (user.name || '')}
                            >
                              {user.firstName && user.lastName 
                                ? `${user.firstName} ${user.lastName}` 
                                : user.name}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span
                                      tabIndex={0}
                                      aria-label={`Email: ${user.email}`}
                                      data-testid={`user-email-${user.id}`}
                                      className="text-sm text-gray-600 truncate min-w-0 max-w-[50ch]"
                                    >
                                      {user.email}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[400px] break-all">
                                    <p>{user.email}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <button
                                onClick={() => copyToClipboard(user.email, 'Email')}
                                className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                                title="Copy email"
                                aria-label={`Copy email ${user.email}`}
                                data-testid={`copy-email-${user.id}`}
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              </div>
                              <div className={hasEmailBadges ? 'mt-1 flex flex-wrap items-center gap-1' : 'hidden'}>
                              {(user.emailDeliverabilityStatus === 'hard_bounced' || user.emailDeliverabilityStatus === 'complained') && (
                                <TooltipProvider delayDuration={200}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span
                                        data-testid={`deliverability-badge-${user.id}`}
                                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                                          user.emailDeliverabilityStatus === 'complained'
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-orange-100 text-orange-700'
                                        }`}
                                      >
                                        <AlertTriangle className="w-3 h-3" />
                                        {user.emailDeliverabilityStatus === 'complained' ? 'Complained' : 'Bounced'}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      <p>Mail delivery blocked: {user.emailDeliverabilityStatus}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {!user.emailVerified && (
                                <span
                                  data-testid={`unverified-badge-${user.id}`}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700"
                                >
                                  <Mail className="w-3 h-3" />
                                  Unverified
                                </span>
                              )}
                              {user.approvalStatus === 'pending' && (
                                <span
                                  data-testid={`pending-badge-${user.id}`}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700"
                                >
                                  Pending Approval
                                </span>
                              )}
                              {user.approvalStatus === 'rejected' && (
                                <span
                                  data-testid={`rejected-badge-${user.id}`}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700"
                                >
                                  Rejected
                                </span>
                              )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5 text-sm text-gray-600 min-w-0">
                              <span
                                className="truncate min-w-0"
                                data-testid={`user-company-${user.id}`}
                                title={user.companyName || 'N/A'}
                              >
                                {user.companyName || 'N/A'}
                              </span>
                              {user.companyDeleted && (
                                <TooltipProvider delayDuration={200}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        data-testid={`deleted-company-icon-${user.id}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/admin/company-settings/list?status=deleted&openCompanyId=${user.companyId}`);
                                        }}
                                        className="text-amber-500 hover:text-amber-600 cursor-pointer transition-colors shrink-0"
                                      >
                                        <AlertCircle className="w-4 h-4" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      <p>This company has been deleted.</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className="inline-flex max-w-full truncate px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                              title={user.companyType || 'N/A'}
                            >
                              {user.companyType || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center">
                              {isAdminRole(user.role) ? (
                                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-100" title="Admin">
                                  <Shield className="w-5 h-5 text-orange-600" />
                                </div>
                              ) : (
                                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100" title="User">
                                  <User className="w-5 h-5 text-gray-600" />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center">
                              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-800 font-semibold text-sm">
                                {user.projectCount || 0}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-3">
                              <button
                                onClick={() => handleViewToggle(user.id)}
                                className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                                title="View Details"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleEdit(user)}
                                disabled={deletedFilter === 'deleted'}
                                className={deletedFilter === 'deleted' 
                                  ? "text-gray-300 p-2 rounded-lg cursor-not-allowed" 
                                  : "text-orange-600 hover:text-orange-700 p-2 rounded-lg hover:bg-orange-50 transition-colors"}
                                title={deletedFilter === 'deleted' ? "Cannot edit deleted users" : "Edit"}
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              {/* Disable delete for current user, super admin, and deleted users */}
                              {user.id !== currentUser?.id && user.email !== 'admin@specard.net' && deletedFilter !== 'deleted' ? (
                                <button
                                  onClick={() => handleDeleteClick(user.id)}
                                  className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              ) : (
                                <button
                                  disabled
                                  className="text-gray-300 p-2 rounded-lg cursor-not-allowed"
                                  title={
                                    deletedFilter === 'deleted' 
                                      ? "Cannot delete already deleted users" 
                                      : user.id === currentUser?.id 
                                        ? "Cannot delete yourself" 
                                        : "Super admin cannot be deleted"
                                  }
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        
                        {/* Expanded View Row */}
                        {expandedRow === user.id && (
                          <tr>
                            <td colSpan="8" className="px-6 py-6 bg-gray-50">
                              <div className="bg-white rounded-xl shadow-lg p-6">
                                <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-orange-200">
                                  <h3 className="text-xl font-bold" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                    User Details
                                  </h3>
                                  <button 
                                    onClick={() => handleViewToggle(user.id)}
                                    className="text-gray-400 hover:text-gray-600"
                                  >
                                    <X className="w-5 h-5" />
                                  </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div className="bg-blue-50 rounded-lg p-5 border-l-4 border-blue-500">
                                    <h4 className="font-bold text-blue-900 mb-4">Personal Information</h4>
                                    <dl className="space-y-2">
                                      <div>
                                        <dt className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Full Name</dt>
                                        <dd className="text-sm text-gray-900 font-medium mt-1">
                                          {user.firstName && user.lastName 
                                            ? `${user.firstName} ${user.lastName}` 
                                            : user.name}
                                        </dd>
                                      </div>
                                      <div>
                                        <dt className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Email</dt>
                                        <dd className="text-sm text-gray-900 mt-1 flex items-center gap-2">
                                          {user.email}
                                          <button
                                            onClick={() => copyToClipboard(user.email, 'Email')}
                                            className="text-gray-400 hover:text-gray-600 transition-colors"
                                            title="Copy email"
                                          >
                                            <Copy className="w-4 h-4" />
                                          </button>
                                        </dd>
                                      </div>
                                      <div>
                                        <dt className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Role</dt>
                                        <dd className="text-sm text-gray-900 mt-1">{formatRole(user.role)}</dd>
                                      </div>
                                    </dl>
                                  </div>

                                  <div className="bg-green-50 rounded-lg p-5 border-l-4 border-green-500">
                                    <h4 className="font-bold text-green-900 mb-4">Company Information</h4>
                                    <dl className="space-y-2">
                                      <div>
                                        <dt className="text-xs font-semibold text-green-700 uppercase tracking-wide">Company</dt>
                                        <dd className="text-sm text-gray-900 font-medium mt-1">{user.companyName || 'N/A'}</dd>
                                      </div>
                                      <div>
                                        <dt className="text-xs font-semibold text-green-700 uppercase tracking-wide">Created At</dt>
                                        <dd className="text-sm text-gray-900 mt-1">
                                          {new Date(user.createdAt).toLocaleDateString()}
                                        </dd>
                                      </div>
                                      <div>
                                        <dt className="text-xs font-semibold text-green-700 uppercase tracking-wide">Status</dt>
                                        <dd className="text-sm mt-1">
                                          {user.isActive === false ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                              <Power className="w-3 h-3" /> Disabled
                                            </span>
                                          ) : user.status === 'FROZEN' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                              <Ban className="w-3 h-3" /> Frozen
                                              {user.freezeUntil && (
                                                <span className="ml-1 text-red-500">
                                                  (until {new Date(user.freezeUntil).toLocaleString()})
                                                </span>
                                              )}
                                            </span>
                                          ) : user.status === 'SUSPENDED' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                              <ShieldAlert className="w-3 h-3" /> Suspended
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                              <UserCheck className="w-3 h-3" /> Active
                                            </span>
                                          )}
                                        </dd>
                                      </div>
                                      {user.isActive === false && user.disabledAt && (
                                        <div>
                                          <dt className="text-xs font-semibold text-green-700 uppercase tracking-wide">Disabled At</dt>
                                          <dd className="text-sm text-gray-900 mt-1">
                                            {new Date(user.disabledAt).toLocaleDateString()}
                                            <span className="text-xs text-orange-600 ml-2">
                                              (Auto-delete on {new Date(new Date(user.disabledAt).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()})
                                            </span>
                                          </dd>
                                        </div>
                                      )}
                                      {/* Email Deliverability */}
                                      <div>
                                        <dt className="text-xs font-semibold text-green-700 uppercase tracking-wide">Email Deliverability</dt>
                                        <dd className="text-sm mt-1 flex items-center gap-2">
                                          {user.emailDeliverabilityStatus === 'hard_bounced' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700" data-testid={`detail-deliverability-${user.id}`}>
                                              <AlertTriangle className="w-3 h-3" /> Hard Bounced
                                            </span>
                                          ) : user.emailDeliverabilityStatus === 'complained' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700" data-testid={`detail-deliverability-${user.id}`}>
                                              <AlertTriangle className="w-3 h-3" /> Complained
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" data-testid={`detail-deliverability-${user.id}`}>
                                              <CheckCircle className="w-3 h-3" /> OK
                                            </span>
                                          )}
                                          {(user.emailDeliverabilityStatus === 'hard_bounced' || user.emailDeliverabilityStatus === 'complained') && (
                                            <button
                                              data-testid={`reset-deliverability-${user.id}`}
                                              onClick={() => handleResetDeliverability(user.id)}
                                              className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                                            >
                                              Reset
                                            </button>
                                          )}
                                          {user.emailDeliverabilityUpdatedAt && (user.emailDeliverabilityStatus === 'hard_bounced' || user.emailDeliverabilityStatus === 'complained') && (
                                            <span className="text-xs text-gray-400">
                                              since {new Date(user.emailDeliverabilityUpdatedAt).toLocaleDateString()}
                                            </span>
                                          )}
                                        </dd>
                                      </div>
                                      {/* Email Verified */}
                                      <div>
                                        <dt className="text-xs font-semibold text-green-700 uppercase tracking-wide">Email Verified</dt>
                                        <dd className="text-sm mt-1">
                                          {user.emailVerified ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" data-testid={`detail-verified-${user.id}`}>
                                              <CheckCircle className="w-3 h-3" /> Verified
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700" data-testid={`detail-verified-${user.id}`}>
                                              <Mail className="w-3 h-3" /> Unverified
                                            </span>
                                          )}
                                        </dd>
                                      </div>
                                      {/* Approval Status */}
                                      <div>
                                        <dt className="text-xs font-semibold text-green-700 uppercase tracking-wide">Approval Status</dt>
                                        <dd className="text-sm mt-1 flex items-center gap-2">
                                          {user.approvalStatus === 'approved' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" data-testid={`detail-approval-${user.id}`}>
                                              <CheckCircle className="w-3 h-3" /> Approved
                                            </span>
                                          ) : user.approvalStatus === 'rejected' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700" data-testid={`detail-approval-${user.id}`}>
                                              Rejected
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700" data-testid={`detail-approval-${user.id}`}>
                                              Pending
                                            </span>
                                          )}
                                          {user.approvalStatus !== 'approved' && !user.deletedAt && (
                                            <button
                                              data-testid={`approve-btn-${user.id}`}
                                              onClick={(e) => { e.stopPropagation(); handleApproveUser(user.id); }}
                                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
                                            >
                                              Approve
                                            </button>
                                          )}
                                          {user.approvalStatus === 'pending' && !user.deletedAt && (
                                            <button
                                              data-testid={`reject-btn-${user.id}`}
                                              onClick={(e) => { e.stopPropagation(); handleRejectUser(user.id); }}
                                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                                            >
                                              Reject
                                            </button>
                                          )}
                                        </dd>
                                      </div>
                                      {user.deletedAt && (
                                        <div>
                                          <dt className="text-xs font-semibold text-green-700 uppercase tracking-wide">Deleted At</dt>
                                          <dd className="text-sm text-gray-900 mt-1">
                                            {new Date(user.deletedAt).toLocaleDateString()}
                                            {user.deletionReason === 'auto_disable_expiry' ? (
                                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                                Auto-deleted (30-day disable expiry)
                                              </span>
                                            ) : (
                                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                                Manually deleted
                                              </span>
                                            )}
                                          </dd>
                                        </div>
                                      )}
                                    </dl>
                                  </div>

                                  {/* Auth Events Section */}
                                  <div className="bg-purple-50 rounded-lg p-5 border-l-4 border-purple-500 md:col-span-2">
                                    <div className="flex items-center justify-between mb-4">
                                      <h4 className="font-bold text-purple-900 flex items-center gap-2">
                                        <Shield className="w-5 h-5" />
                                        Last 5 Security Events
                                      </h4>
                                      <button
                                        onClick={() => handleShowAllEvents(user.id)}
                                        className="text-sm text-purple-600 hover:text-purple-800 underline"
                                      >
                                        All Events
                                      </button>
                                    </div>
                                    
                                    {loadingAuthEvents[user.id] ? (
                                      <div className="flex items-center justify-center py-4">
                                        <RefreshCw className="w-5 h-5 text-purple-500 animate-spin" />
                                      </div>
                                    ) : userAuthEvents[user.id]?.length > 0 ? (
                                      <div className="space-y-2">
                                        {userAuthEvents[user.id].map((event, idx) => {
                                          const config = getEventTypeConfig(event.eventType);
                                          const Icon = config.icon;
                                          return (
                                            <div key={idx} className="flex items-center gap-3 text-sm bg-white rounded-lg px-3 py-2">
                                              <div className={`p-1.5 rounded ${config.bg}`}>
                                                <Icon className={`w-4 h-4 ${config.color}`} />
                                              </div>
                                              <span className="font-medium text-gray-900">{config.label}</span>
                                              <span className="text-gray-500 text-xs ml-auto">
                                                {formatEventDate(event.occurredAt)}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <p className="text-sm text-gray-500 py-2">No events yet</p>
                                    )}
                                  </div>

                                  {/* Admin Actions */}
                                  {!user.isSuperAdmin && user.id !== currentUser?.id && (
                                    <div className="bg-orange-50 rounded-lg p-5 border-l-4 border-orange-500 md:col-span-2">
                                      <h4 className="font-bold text-orange-900 mb-4 flex items-center gap-2">
                                        <Shield className="w-5 h-5" />
                                        Session & Access Management
                                      </h4>
                                      <div className="flex flex-wrap gap-3">
                                        {(() => {
                                          const isDeleted = !!user.deletedAt;
                                          const disabledStyle = isDeleted ? ' opacity-50 cursor-not-allowed' : '';
                                          return (
                                            <>
                                              <button
                                                onClick={() => !isDeleted && handleForceLogout(user.id, user.email)}
                                                disabled={isDeleted}
                                                className={`inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm${disabledStyle}`}
                                              >
                                                <LogOut className="w-4 h-4" />
                                                Force Logout
                                              </button>
                                              
                                              {user.status === 'FROZEN' ? (
                                                <button
                                                  onClick={() => !isDeleted && handleUnfreezeUser(user.id, user.email)}
                                                  disabled={isDeleted}
                                                  className={`inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm${disabledStyle}`}
                                                >
                                                  <UserCheck className="w-4 h-4" />
                                                  Unfreeze Account
                                                </button>
                                              ) : user.status === 'SUSPENDED' ? (
                                                <button
                                                  onClick={() => !isDeleted && handleUnsuspendUser(user.id, user.email)}
                                                  disabled={isDeleted}
                                                  className={`inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm${disabledStyle}`}
                                                >
                                                  <ShieldCheck className="w-4 h-4" />
                                                  Unsuspend Account
                                                </button>
                                              ) : (
                                                <button
                                                  onClick={() => !isDeleted && handleFreezeUser(user.id, user.email)}
                                                  disabled={isDeleted}
                                                  className={`inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm${disabledStyle}`}
                                                >
                                                  <Ban className="w-4 h-4" />
                                                  Freeze Account
                                                </button>
                                              )}

                                              {/* Active/Passive Toggle */}
                                              <button
                                                onClick={() => !isDeleted && handleToggleActive(user.id, user.email, user.isActive !== false)}
                                                disabled={isDeleted || togglingActive[user.id]}
                                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${
                                                  (isDeleted || togglingActive[user.id]) ? 'opacity-50 cursor-not-allowed' : ''
                                                } ${
                                                  user.isActive !== false
                                                    ? 'bg-gray-600 text-white hover:bg-gray-700'
                                                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                                }`}
                                                data-testid={`toggle-active-${user.id}`}
                                              >
                                                <Power className="w-4 h-4" />
                                                {togglingActive[user.id] 
                                                  ? 'Processing...' 
                                                  : user.isActive !== false 
                                                    ? 'Set Passive' 
                                                    : 'Set Active'}
                                              </button>

                                              {/* Impersonate */}
                                              {!user.isSuperAdmin && (
                                                <button
                                                  onClick={() => !isDeleted && user.status !== 'SUSPENDED' && user.isActive !== false && handleImpersonate(user.id, user.email, user.status)}
                                                  disabled={isDeleted || user.status === 'SUSPENDED' || user.isActive === false}
                                                  className={`inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm ${
                                                    (isDeleted || user.status === 'SUSPENDED' || user.isActive === false) ? 'opacity-50 cursor-not-allowed' : ''
                                                  }`}
                                                  data-testid={`impersonate-${user.id}`}
                                                >
                                                  <UserCog className="w-4 h-4" />
                                                  Impersonate
                                                </button>
                                              )}
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                </div>

                {/* Pagination */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.total}</span> users
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    {[...Array(pagination.totalPages)].map((_, idx) => {
                      const pageNum = idx + 1;
                      if (
                        pageNum === 1 ||
                        pageNum === pagination.totalPages ||
                        (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                              pagination.page === pageNum
                                ? 'text-white'
                                : 'border border-gray-300 hover:bg-gray-100'
                            }`}
                            style={pagination.page === pageNum ? { background: '#FF6600' } : {}}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (pageNum === pagination.page - 2 || pageNum === pagination.page + 2) {
                        return <span key={pageNum} className="px-2">...</span>;
                      }
                      return null;
                    })}

                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Edit Modal - Continue in next message due to length */}
          {editingUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                  <h3 className="text-xl font-bold" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Edit User
                  </h3>
                  <button onClick={handleCloseEdit} className="text-gray-400 hover:text-gray-600">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <form onSubmit={handleUpdate} className="p-6 space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                      <input
                        type="text"
                        required
                        value={editingUser.firstName || ''}
                        onChange={(e) => setEditingUser({...editingUser, firstName: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                      <input
                        type="text"
                        required
                        value={editingUser.lastName || ''}
                        onChange={(e) => setEditingUser({...editingUser, lastName: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={editingUser.email}
                      onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  {/* Role - Moved above Company */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
                    <select
                      required
                      value={editingUser.role}
                      onChange={(e) => {
                        const newRole = e.target.value;
                        setEditingUser({
                          ...editingUser, 
                          role: newRole,
                          // Clear company if owner selected
                          ...(newRole === 'owner' ? { companyId: '', companyName: '' } : {})
                        });
                        // Clear company search if owner selected
                        if (newRole === 'owner') {
                          setEditCompanySearch('');
                        }
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="owner">Owner</option>
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                    </select>
                  </div>

                  {/* Company - Now below Role */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company
                    </label>
                    {!editingUser.emailVerified && !editingUser.isSuperAdmin && editingUser.role !== 'owner' && !editingUser.companyId && (
                      <p className="text-xs text-amber-600 mb-1 flex items-center gap-1" data-testid="unverified-company-warning">
                        <AlertTriangle className="w-3 h-3" />
                        Email not verified. Verify email to assign a company.
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        disabled={editingUser.role === 'owner' || (!editingUser.emailVerified && !editingUser.isSuperAdmin && editingUser.role !== 'owner')}
                        value={editCompanySearch}
                        onChange={(e) => {
                          setEditCompanySearch(e.target.value);
                          if (e.target.value && editFilteredCompanies.length > 0) {
                            setShowEditCompanyDropdown(true);
                          }
                        }}
                        onFocus={() => {
                          if (editCompanySearch && editFilteredCompanies.length > 0) {
                            setShowEditCompanyDropdown(true);
                          }
                        }}
                        onKeyDown={handleEditCompanyKeyDown}
                        className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                          editingUser.role === 'owner' || (!editingUser.emailVerified && !editingUser.isSuperAdmin) ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        placeholder={editingUser.role === 'owner' ? 'N/A for Owner role' : (!editingUser.emailVerified && !editingUser.isSuperAdmin) ? 'Verify email first' : 'Search for a company...'}
                      />
                      {editingUser.companyId && editingUser.role !== 'owner' && (
                        <button
                          type="button"
                          data-testid="remove-company-btn"
                          onClick={() => {
                            setConfirmModal({
                              isOpen: true,
                              title: 'Remove Company',
                              message: `Remove company assignment "${editingUser.companyName || 'Unknown'}" from this user?`,
                              type: 'warning',
                              confirmText: 'Remove',
                              showCancel: true,
                              onConfirm: () => {
                                setEditingUser({ ...editingUser, companyId: '', companyName: '' });
                                setEditCompanySearch('');
                              }
                            });
                          }}
                          className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove company assignment"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {showEditCompanyDropdown && editFilteredCompanies.length > 0 && editingUser.role !== 'owner' && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {editFilteredCompanies.map((company, index) => (
                          <button
                            key={company.id}
                            type="button"
                            onClick={() => selectCompanyForEdit(company)}
                            className={`w-full text-left px-4 py-3 transition-colors ${
                              index === selectedEditCompanyIndex 
                                ? 'bg-orange-100 text-orange-900' 
                                : 'hover:bg-orange-50'
                            }`}
                          >
                            {company.companyName}
                          </button>
                        ))}
                      </div>
                    )}
                    {editingUser.companyId && editingUser.role !== 'owner' && (
                      <p className="text-sm text-green-600 mt-1">Company selected: {editingUser.companyName}</p>
                    )}
                  </div>

                  {/* Password Change Section */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-700 mb-4">Change Password (Optional)</h4>
                    <p className="text-sm text-gray-500 mb-4">Leave blank to keep current password</p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                        <PasswordInput
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password (min 6 characters)"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                        <PasswordInput
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Re-enter new password"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 border-t">
                    <button
                      type="button"
                      onClick={handleCloseEdit}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-all"
                      style={{ background: '#FF6600' }}
                    >
                      Update User
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* All Events Modal */}
          {showAllEventsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
                <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-purple-50">
                  <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    All Security Events
                  </h3>
                  <button
                    onClick={() => setShowAllEventsModal(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                  {loadingAllEvents ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
                    </div>
                  ) : allEventsData.events?.length > 0 ? (
                    <div className="space-y-3">
                      {allEventsData.events.map((event, idx) => {
                        const config = getEventTypeConfig(event.eventType);
                        const Icon = config.icon;
                        return (
                          <div key={idx} className="flex items-center gap-3 text-sm bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <div className={`p-2 rounded-lg ${config.bg}`}>
                              <Icon className={`w-5 h-5 ${config.color}`} />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{config.label}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                IP: {event.ipAddress || '-'}
                              </div>
                            </div>
                            <span className="text-gray-500 text-xs">
                              {formatEventDate(event.occurredAt)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-8">No events yet</p>
                  )}
                </div>
                
                {/* Pagination */}
                {allEventsData.totalPages > 1 && (
                  <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                    <div className="text-sm text-gray-600">
                      Total {allEventsData.total} records
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => loadAllUserEvents(showAllEventsModal, allEventsData.page - 1)}
                        disabled={allEventsData.page === 1}
                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-gray-700">
                        Page {allEventsData.page} / {allEventsData.totalPages}
                      </span>
                      <button
                        onClick={() => loadAllUserEvents(showAllEventsModal, allEventsData.page + 1)}
                        disabled={allEventsData.page === allEventsData.totalPages}
                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Confirmation Modal */}
          <ConfirmationModal
            isOpen={confirmModal.isOpen}
            onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
            onConfirm={confirmModal.onConfirm}
            title={confirmModal.title}
            message={confirmModal.message}
            type={confirmModal.type}
            confirmText={confirmModal.confirmText}
            cancelText={confirmModal.cancelText}
            showCancel={confirmModal.showCancel}
          />

          {/* Freeze Duration Modal */}
          {freezeModal.isOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !freezeModal.loading && setFreezeModal(prev => ({ ...prev, isOpen: false }))}>
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()} data-testid="freeze-duration-modal">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Freeze Account</h3>
                <p className="text-sm text-gray-500 mb-4">{freezeModal.userEmail}</p>
                
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
                  The user account will be automatically reactivated after the selected duration.
                </p>

                <div className="mb-5">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Freeze Duration</label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Hours</label>
                      <select
                        value={freezeModal.hours}
                        onChange={(e) => setFreezeModal(prev => ({ ...prev, hours: parseInt(e.target.value) }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="freeze-hours-select"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>{i}</option>
                        ))}
                      </select>
                    </div>
                    <span className="text-gray-400 font-medium mt-5">:</span>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Minutes</label>
                      <select
                        value={freezeModal.minutes}
                        onChange={(e) => setFreezeModal(prev => ({ ...prev, minutes: parseInt(e.target.value) }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="freeze-minutes-select"
                      >
                        <option value={0}>0</option>
                        <option value={15}>15</option>
                        <option value={30}>30</option>
                        <option value={45}>45</option>
                      </select>
                    </div>
                  </div>
                  {freezeModal.hours === 0 && freezeModal.minutes === 0 && (
                    <p className="text-xs text-red-500 mt-2">Freeze duration must be greater than 0</p>
                  )}
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setFreezeModal(prev => ({ ...prev, isOpen: false }))}
                    disabled={freezeModal.loading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmFreeze}
                    disabled={freezeModal.loading || (freezeModal.hours === 0 && freezeModal.minutes === 0)}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    data-testid="freeze-confirm-btn"
                  >
                    {freezeModal.loading ? (
                      <><RefreshCw className="w-4 h-4 animate-spin" /> Freezing...</>
                    ) : (
                      <><Ban className="w-4 h-4" /> Confirm Freeze</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ListUsers;
